/* eslint-disable @typescript-eslint/no-explicit-any */
import { getAppMetadata } from './metadata/metadata'
import { Package, Plan } from '@workspaces/types'
import { isPlanPackageExpiredForNoAgencyConfirmation } from './filter/packages.helper'

function deserializeCountries(countries: any[]) {
  // validate no null values in the country array
  const checkedCountries = countries.filter((c) => c)
  if (!checkedCountries.length) {
    return getAppMetadata().countries.map((c) => c.id)
  }
  return checkedCountries
}

function deserializeDigitalPanel(isDigital: any) {
  return isDigital === '' ? undefined : isDigital
}

function deserializeSubfilterPackage(thePackage: any) {
  return thePackage === '' ? null : thePackage
}

function parsePlanDate(value: any): Date {
  // Calendar dates are stored as timezone-less 'YYYY-MM-DD' strings; parse
  // them as local midnight so every viewer sees the same day. `new Date()`
  // would parse them as UTC midnight, shifting the day west of UTC.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number)
    return new Date(year, month - 1, day)
  }
  // Legacy plans stored full ISO timestamps; keep parsing them as instants.
  return new Date(value)
}

function removeUndefinedProperty<T>(key: string, obj: T): T {
  const keyTyped: keyof T = key as unknown as keyof T
  if (obj[keyTyped] === undefined) {
    delete obj[keyTyped]
  }
  return obj
}

export function deserializeFilters(filters: any) {
  const objFilters = JSON.parse(filters)

  return objFilters.map((subfilter: any) => {
    const deserialized = {
      tabname: subfilter.tabname || '',
      countries: deserializeCountries(subfilter.countries),
      types: subfilter.types,
      assets_excluded: subfilter.excluded || [],
      assets_included: subfilter.included || [],
      panels: subfilter.panels,
      panelsExcluded: subfilter.panelsExcluded,
      package: deserializeSubfilterPackage(subfilter.package || ''),
      isDigital: deserializeDigitalPanel(subfilter.is_digital),
    }

    for (const key in deserialized) {
      removeUndefinedProperty(key, deserialized)
    }

    return deserialized
  })
}

export function deserializeLegend(legend: Plan.Legend): Plan.Legend {
  if (legend) {
    return {
      ...legend,
      boundariesVisibility: legend.boundariesVisibility !== false,
      assetsIdsLayerVisibility: legend.assetsIdsLayerVisibility === true,
      colorizeAssets: legend.colorizeAssets === true,
      angleNorthMap: legend.angleNorthMap === true,
    }
  } else {
    return {
      boundariesVisibility: true,
      assetsIdsLayerVisibility: false,
      colorizeAssets: false,
      productClass: 'l2product_id',
      facingOrientationsIcons: false,
      angleNorthMap: false,
      hiddenProductsId: [],
    }
  }
}

export function deserializePackage(
  packageFilter: string | undefined,
): Package.PlanPackage | null {
  if (!packageFilter || packageFilter === 'null') {
    return null
  }

  const jsonPackageFilter = JSON.parse(packageFilter)

  const deserialized: Package.PlanPackage = {
    state: jsonPackageFilter.state,
    packages: jsonPackageFilter.packages,
    price: jsonPackageFilter.price,
    assets: jsonPackageFilter.assets,
    impressions: jsonPackageFilter.impressions,
    availability: jsonPackageFilter.availability,
    filter: jsonPackageFilter.filter,
    bookedConfirmByAdminAt: jsonPackageFilter.bookedConfirmByAdminAt
      ? new Date(jsonPackageFilter.bookedConfirmByAdminAt)
      : undefined,
    expirationForAgencyBookingConfirmation:
      jsonPackageFilter.expirationForAgencyBookingConfirmation
        ? new Date(jsonPackageFilter.expirationForAgencyBookingConfirmation)
        : undefined,
    bookedFinalizedAt: jsonPackageFilter.bookedFinalizedAt
      ? new Date(jsonPackageFilter.bookedFinalizedAt)
      : undefined,
    expirationForAgencyCancellation:
      jsonPackageFilter.expirationForAgencyCancellation
        ? new Date(jsonPackageFilter.expirationForAgencyCancellation)
        : undefined,
    actions: [],
  }

  if (jsonPackageFilter.actions) {
    deserialized.actions = jsonPackageFilter.actions.map((action: any) => {
      return {
        state: action.state,
        timestamp: new Date(action.timestamp),
        user: action.user,
      }
    })
  }

  const dateRange = jsonPackageFilter.filter.dateRange
  if (Array.isArray(dateRange) && dateRange.length === 2) {
    deserialized.filter.dateRange = [
      parsePlanDate(dateRange[0]),
      parsePlanDate(dateRange[1]),
    ]
  } else {
    deserialized.filter.dateRange = null
  }
  deserialized.filter.panelType = jsonPackageFilter.filter.panelType
  deserialized.filter.priceRange = jsonPackageFilter.filter.priceRange

  if (isPlanPackageExpiredForNoAgencyConfirmation(deserialized)) {
    deserialized.state = Package.State.Expired
  }

  return deserialized
}
